import { createContext, useState, useContext, useEffect } from 'react';

// API key storage configuration
const API_KEY_STORAGE_KEY = 'mermaid-diagram-assistant-api-key';

// Create the context
const ApiKeyContext = createContext(null);

// Hook for using the API key context
export const useApiKey = () => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKey must be used within an ApiKeyProvider');
  }
  return context;
};

// Provider component
export const ApiKeyProvider = ({ children }) => {
  const [apiKey, setApiKeyState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Initialize API key from localStorage
  useEffect(() => {
    try {
      const storedKey = localStorage.getItem(API_KEY_STORAGE_KEY);
      setApiKeyState(storedKey);
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Function to set the API key
  const setApiKey = (key) => {
    try {
      if (key) {
        localStorage.setItem(API_KEY_STORAGE_KEY, key);
      } else {
        localStorage.removeItem(API_KEY_STORAGE_KEY);
      }
      setApiKeyState(key);
      return true;
    } catch (error) {
      console.error('Error storing API key:', error);
      return false;
    }
  };
  
  // Check if API key is set
  const hasApiKey = () => {
    return apiKey !== null;
  };
  
  // Clear the API key
  const clearApiKey = () => {
    setApiKey(null);
  };
  
  // Get the current API key
  const getApiKey = () => {
    return apiKey;
  };
  
  // Value object to be provided by the context
  const value = {
    apiKey,
    setApiKey,
    hasApiKey,
    clearApiKey,
    getApiKey,
    isLoading
  };
  
  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
};

export default ApiKeyContext; 